/**
 * Spec 04 · Iconography.
 *
 * Monoline, 1.5px stroke, 24px box, square caps, no fills. Ink by
 * default (currentColor); accent only when active. Sizes: 20px inline
 * with labels, 24px standalone, 22px in mobile chrome inside a 44×44
 * tap box.
 *
 * Paths are lifted verbatim from the design system so the silhouettes
 * match the spec sheet rather than approximating it.
 */

type IconProps = {
  size?: number
  className?: string
  strokeWidth?: number
}

function Svg({
  size = 24,
  className,
  strokeWidth = 1.5,
  children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="square"
      strokeLinejoin="miter"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

export function BagIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 7h16l-1.4 13H5.4L4 7z" />
      <path d="M8.5 7V5.5a3.5 3.5 0 017 0V7" />
    </Svg>
  )
}

export function MenuIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 7h18M3 12h18M3 17h18" />
    </Svg>
  )
}

export function CloseIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5 5l14 14M19 5L5 19" />
    </Svg>
  )
}

export function ArrowLeftIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M20 12H4M10 6l-6 6 6 6" />
    </Svg>
  )
}

export function ArrowRightIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 12h16M14 6l6 6-6 6" />
    </Svg>
  )
}

export function SunIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1" />
    </Svg>
  )
}

export function MoonIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z" />
    </Svg>
  )
}

export function ImagePlaceholderIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3.5" y="4.5" width="17" height="15" />
      <path d="M3.5 16l4.5-4.5 4 4 3-3 4.5 4.5" />
      <circle cx="9" cy="9" r="1.4" />
    </Svg>
  )
}

/** The tick used by checkboxes, selected option cards and confirmation. */
export function CheckIcon({ strokeWidth = 2.5, ...props }: IconProps) {
  return (
    <Svg strokeWidth={strokeWidth} {...props}>
      <path d="M5 12.5l4.5 4.5L19 7" />
    </Svg>
  )
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6 9l6 6 6-6" />
    </Svg>
  )
}

/** Pencil — the quiet "change this" control beside a committed value. */
export function EditIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 20h4l10-10-4-4L4 16v4z" />
      <path d="M14.5 5.5l4 4" />
    </Svg>
  )
}

/** Flame — the "working flame" badge on the product page. */
export function FlameIcon(props: IconProps) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path
        strokeLinejoin="round"
        d="M12 3.5c2.6 3.1 5.5 5.6 5.5 9a5.5 5.5 0 11-11 0c0-3.4 2.9-5.9 5.5-9z"
      />
    </Svg>
  )
}

/** Two circles distinguish the one-of-a-kind badge from painting and resin. */
export function UniqueIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="8.2" cy="12" r="6.15" />
      <circle cx="15.4" cy="12" r="6.15" />
    </Svg>
  )
}

/** Gloss — circle with a light arc, the "sealed glossy" badge. */
export function GlossIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="7.5" />
      <path d="M8.5 9.5a4.5 4.5 0 013-2" />
    </Svg>
  )
}

/** Brush — the "hand-painted" badge on the product page. */
export function BrushIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 19c3-1 4-3 6-6s4-6 8-9c-1 4-3 6-6 8s-5 4-8 7z" />
      <path d="M4 19l3-3" />
    </Svg>
  )
}

/** Instagram — the club band's social tile. */
export function InstagramIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="0.6" fill="currentColor" />
    </Svg>
  )
}

/** TikTok — the club band's social tile. */
export function TikTokIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M14 4v9.5a3.75 3.75 0 11-3.75-3.75" />
      <path d="M14 5.5c0.8 2.2 2.4 3.6 4.5 3.9" />
    </Svg>
  )
}

/** Bin — the "take this back off" control beside the pencil. */
export function TrashIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 7h16" />
      <path d="M6.5 7l1 13h9l1-13" />
      <path d="M9.5 7V4.5h5V7" />
      <path d="M10.5 10.5v6M13.5 10.5v6" />
    </Svg>
  )
}
